import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Calculator,
  Download,
  ExternalLink,
  FileText,
  Paperclip,
  Search,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { downloadBase64File, readFileAsBase64 } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function AccountingSystemsPage() {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("LT");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editCountry, setEditCountry] = useState("");
  const [editApiDocUrl, setEditApiDocUrl] = useState("");
  const [editApiDoc, setEditApiDoc] = useState("");
  const [docsForSystem, setDocsForSystem] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const list = trpc.accountingSystems.list.useQuery();
  const allDocs = trpc.documents.list.useQuery({});

  const docCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of allDocs.data ?? []) {
      if (!d.accountingSystemId) continue;
      map.set(d.accountingSystemId, (map.get(d.accountingSystemId) ?? 0) + 1);
    }
    return map;
  }, [allDocs.data]);

  const create = trpc.accountingSystems.create.useMutation({
    onSuccess: () => {
      utils.accountingSystems.list.invalidate();
      setName("");
      setCountry("LT");
      setCreateOpen(false);
      toast.success("Accounting system added");
    },
    onError: (err) => toast.error(err.message || "Failed to add"),
  });

  const update = trpc.accountingSystems.update.useMutation({
    onSuccess: () => {
      utils.accountingSystems.list.invalidate();
      setEditingId(null);
      toast.success("Updated");
    },
    onError: () => toast.error("Failed to update"),
  });

  const remove = trpc.accountingSystems.delete.useMutation({
    onSuccess: () => {
      utils.accountingSystems.list.invalidate();
      toast.success("Removed");
    },
    onError: () => toast.error("Failed to remove"),
  });

  const allItems = list.data ?? [];
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((s) => {
      const haystack = [
        s.name,
        s.country,
        s.apiDocumentationUrl ?? "",
        s.apiDocumentation ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allItems, search]);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    const c = country.trim().toUpperCase();
    if (!n || c.length < 2) return;
    create.mutate({ name: n, country: c });
  };

  const openCreate = () => {
    setName("");
    setCountry("LT");
    setCreateOpen(true);
  };

  const startEdit = (
    id: string,
    currentName: string,
    currentCountry: string,
    currentApiDocUrl: string | null,
    currentApiDoc: string | null
  ) => {
    setEditingId(id);
    setEditName(currentName);
    setEditCountry(currentCountry);
    setEditApiDocUrl(currentApiDocUrl ?? "");
    setEditApiDoc(currentApiDoc ?? "");
  };

  const saveEdit = (id: string) => {
    const n = editName.trim();
    const c = editCountry.trim().toUpperCase();
    if (!n || c.length < 2) return;
    const url = editApiDocUrl.trim();
    const doc = editApiDoc.trim();
    update.mutate({
      id,
      patch: {
        name: n,
        country: c,
        apiDocumentationUrl: url ? url : null,
        apiDocumentation: doc ? doc : null,
      },
    });
  };

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-8 md:px-6">
      <div className="mb-6 flex items-center gap-3">
        <Calculator className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Accounting systems</h1>
          <p className="text-sm text-muted-foreground">
            Accounting systems that can be assigned to opportunities.
          </p>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative w-full sm:flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, country, documentation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button type="button" onClick={openCreate} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Create new
        </Button>
      </div>

      {list.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : allItems.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          No accounting systems
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          No accounting systems match “{search}”.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[640px]">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Country</TableHead>
                <TableHead>API documentation</TableHead>
                <TableHead className="w-32">Documents</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => {
                const isEditing = editingId === s.id;
                return (
                  <TableRow key={s.id} className="align-top">
                    <TableCell className="font-medium">
                      {isEditing ? (
                        <Input
                          autoFocus
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8"
                        />
                      ) : (
                        s.name
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {isEditing ? (
                        <Input
                          value={editCountry}
                          onChange={(e) => setEditCountry(e.target.value)}
                          maxLength={3}
                          className="h-8 w-20"
                        />
                      ) : (
                        s.country
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col gap-1">
                            <Label
                              htmlFor={`api-doc-url-${s.id}`}
                              className="text-xs text-muted-foreground"
                            >
                              URL (optional)
                            </Label>
                            <Input
                              id={`api-doc-url-${s.id}`}
                              type="url"
                              value={editApiDocUrl}
                              onChange={(e) => setEditApiDocUrl(e.target.value)}
                              placeholder="https://…"
                              className="h-8"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <Label
                              htmlFor={`api-doc-${s.id}`}
                              className="text-xs text-muted-foreground"
                            >
                              Notes / documentation (optional)
                            </Label>
                            <Textarea
                              id={`api-doc-${s.id}`}
                              value={editApiDoc}
                              onChange={(e) => setEditApiDoc(e.target.value)}
                              rows={3}
                              placeholder="Authentication, endpoints, examples…"
                            />
                          </div>
                        </div>
                      ) : s.apiDocumentationUrl || s.apiDocumentation ? (
                        <div className="space-y-1 text-sm">
                          {s.apiDocumentationUrl ? (
                            <a
                              href={s.apiDocumentationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              <span className="truncate">{s.apiDocumentationUrl}</span>
                            </a>
                          ) : null}
                          {s.apiDocumentation ? (
                            <p className="whitespace-pre-wrap text-xs text-muted-foreground">
                              {s.apiDocumentation}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 text-xs"
                        onClick={() => setDocsForSystem({ id: s.id, name: s.name })}
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        <span>Documents</span>
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground tabular-nums">
                          {docCounts.get(s.id) ?? 0}
                        </span>
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              disabled={update.isPending}
                              onClick={() => saveEdit(s.id)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground"
                              onClick={() =>
                                startEdit(
                                  s.id,
                                  s.name,
                                  s.country,
                                  s.apiDocumentationUrl,
                                  s.apiDocumentation
                                )
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  disabled={remove.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    <span className="font-medium">{s.name}</span> will be
                                    removed from all opportunities it was assigned to.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => remove.mutate({ id: s.id })}
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(o) => {
          setCreateOpen(o);
          if (!o) {
            setName("");
            setCountry("LT");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New accounting system</DialogTitle>
            <DialogDescription>
              Add a system that can be assigned to opportunities.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAdd(e);
            }}
            className="space-y-3"
          >
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-as-name" className="text-xs text-muted-foreground">
                Name
              </Label>
              <Input
                id="new-as-name"
                autoFocus
                placeholder="e.g. QuickBooks"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-as-country" className="text-xs text-muted-foreground">
                Country
              </Label>
              <Input
                id="new-as-country"
                placeholder="LT"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                maxLength={3}
                className="w-24"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  create.isPending || !name.trim() || country.trim().length < 2
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {create.isPending ? "Adding…" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DocumentsDialog
        system={docsForSystem}
        onOpenChange={(open) => {
          if (!open) setDocsForSystem(null);
        }}
      />
    </div>
  );
}

type PreviewKind = "image" | "pdf" | "other";

function detectPreviewKind(fileName: string | null, mimeType: string | null): PreviewKind {
  if (mimeType) {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "application/pdf") return "pdf";
  }
  if (fileName) {
    const lower = fileName.toLowerCase();
    if (/\.(png|jpe?g|gif|webp|svg|bmp|avif)$/.test(lower)) return "image";
    if (/\.pdf$/.test(lower)) return "pdf";
  }
  return "other";
}

function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function DocumentsDialog({
  system,
  onOpenChange,
}: {
  system: { id: string; name: string } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const open = system !== null;
  const docsQuery = trpc.documents.list.useQuery(
    { accountingSystemId: system?.id },
    { enabled: open }
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");

  const resetForm = () => {
    setName("");
    setFile(null);
    setDescription("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const create = trpc.documents.create.useMutation({
    onSuccess: () => {
      utils.documents.list.invalidate();
      if (system) utils.documents.list.invalidate({ accountingSystemId: system.id });
      resetForm();
      toast.success("Document uploaded");
    },
    onError: (e) => toast.error(e.message || "Failed to upload document"),
  });

  const remove = trpc.documents.delete.useMutation({
    onSuccess: () => {
      utils.documents.list.invalidate();
      if (system) utils.documents.list.invalidate({ accountingSystemId: system.id });
      toast.success("Removed");
    },
    onError: () => toast.error("Failed to remove"),
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!system || !file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB).`);
      return;
    }
    const displayName = name.trim() || file.name;
    const d = description.trim();
    try {
      const base64 = await readFileAsBase64(file);
      create.mutate({
        name: displayName,
        accountingSystemId: system.id,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        fileBase64: base64,
        description: d ? d : null,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to read file");
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const data = await utils.documents.download.fetch({ id });
      downloadBase64File(data.contentBase64, data.fileName, data.mimeType);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const docs = docsQuery.data ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) resetForm();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            Documents — {system?.name ?? ""}
          </DialogTitle>
          <DialogDescription>
            Upload reference documents (PDFs, images, spreadsheets, etc.). Files are stored
            directly in the database — max {Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB
            per file.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleAdd} className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="doc-file" className="text-xs text-muted-foreground">
              File
            </Label>
            <Input
              id="doc-file"
              ref={fileInputRef}
              type="file"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f && !name.trim()) setName(f.name);
              }}
              required
              className="h-8"
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {formatBytes(file.size)}
              </p>
            )}
          </div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Display name (defaults to filename)"
            className="h-8"
          />
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Description (optional)"
          />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={create.isPending || !file}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {create.isPending ? "Uploading…" : "Upload document"}
            </Button>
          </div>
        </form>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {docsQuery.isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : docs.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No documents yet.
            </p>
          ) : (
            <ul className="space-y-3">
              {docs.map((d) => {
                const hasBinary = !!d.fileName && (d.fileSize ?? 0) > 0;
                const legacyUrl = !hasBinary ? (d.documentUrl ?? d.fileUrl) : null;
                const kind = detectPreviewKind(d.fileName, d.mimeType);
                return (
                  <li
                    key={d.id}
                    className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-start"
                  >
                    <div className="flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/40">
                      {legacyUrl && kind === "image" ? (
                        <img
                          src={legacyUrl}
                          alt={d.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : legacyUrl && kind === "pdf" ? (
                        <iframe
                          src={legacyUrl}
                          title={d.name}
                          className="h-full w-full"
                        />
                      ) : (
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{d.name}</p>
                          {hasBinary ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              <span className="font-mono">{d.fileName}</span>
                              {" · "}
                              {formatBytes(d.fileSize)}
                            </p>
                          ) : legacyUrl ? (
                            <a
                              href={legacyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              <span className="truncate">{legacyUrl}</span>
                            </a>
                          ) : null}
                          {d.description ? (
                            <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                              {d.description}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {hasBinary && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-muted-foreground"
                              onClick={() => handleDownload(d.id)}
                              aria-label="Download"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => remove.mutate({ id: d.id })}
                            disabled={remove.isPending}
                            aria-label="Remove"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
